import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OrderBookComponent } from '@shared/components/trading/common/order-book/order-book.component'
import { TranslatePipe } from '@ngx-translate/core'
import { Subject, takeUntil } from 'rxjs'
import { OptionsHelperService } from '@modules/options-module/general/services/options-helper.service'
import { TuiSkeleton } from '@taiga-ui/kit'
import { EmptyTableComponent } from '@shared/components/common/empty/empty-table.component'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { DatePipe, UpperCasePipe } from '@angular/common'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { OptionContractInterface } from '@modules/options-module/general/interfaces/options-contract.interface'
import { LiveTickerOrderBookInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker-order-book.interface'
import { AnimatedNumberDirective } from '@shared/directives/animated-number/animated-number.directive'

@UseLocales({ ru, en })
@Component({
	selector: 'app-info-options',
	templateUrl: './info-options.component.html',
	styleUrl: './info-options.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		OrderBookComponent,
		TranslatePipe,
		TuiSkeleton,
		EmptyTableComponent,
		HighlightDirective,
		DatePipe,
		UpperCasePipe,
		PricePipe,
		QuantityPipe,
		AnimatedNumberDirective,
	],
})
export class InfoOptionsComponent implements OnInit, OnDestroy {
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly math: Math = Math

	public widget: InputSignal<WidgetInterface | null> = input<WidgetInterface | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _optionsHelperService: OptionsHelperService = inject<OptionsHelperService>(OptionsHelperService)

	protected optionContract: WritableSignal<OptionContractInterface | null> = signal<OptionContractInterface | null>(
		null,
	)
	protected isLoading: WritableSignal<boolean> = signal<boolean>(false)

	protected orderBook: Signal<LiveTickerOrderBookInterface | null> = computed(
		(): LiveTickerOrderBookInterface | null => {
			const optionContract: OptionContractInterface | null = this.optionContract()
			if (!optionContract) return null
			const { ask, bid, ask_size: askSize, bid_size: bidSize } = optionContract.last_quote
			const diffPercent: number = this.math.abs(((ask - bid) / bid) * 100) || 0

			return {
				ask,
				askSize,
				bid,
				bidSize,
				diffPercent,
			}
		},
	)
	private _listenOptionContractDetails(): void {
		this._optionsHelperService.activeOptionContract$
			.pipe(takeUntil(this._destroy$))
			.subscribe((response: OptionContractInterface | null): void => {
				this.optionContract.set(response)
			})
	}

	ngOnInit(): void {
		this._listenOptionContractDetails()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
